import { useState } from "react";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function TradeInPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    vin: "", make: "", model: "", year: 2020, mileage: 50000,
    condition: "good", accident_history: false, loan_payoff: 0,
  });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data: cust } = await api.post("/customers", { full_name: form.full_name, email: form.email, phone: form.phone });
      await api.post("/trade-ins", {
        customer_id: cust.id, vin: form.vin, make: form.make, model: form.model,
        year: Number(form.year), mileage: Number(form.mileage), condition: form.condition,
        accident_history: form.accident_history, loan_payoff: Number(form.loan_payoff),
        photos: [],
      });
      setSubmitted(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  if (submitted) return (
    <PublicShell><section className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center">
      <CheckCircle2 className="w-16 h-16 mx-auto mb-6" style={{ color: "var(--wakzy-gold)" }} />
      <h1 className="font-heading text-4xl mb-3 chrome-text inline-block">Appraisal request received.</h1>
      <p className="text-slate-300">Our appraisal team will email you a written offer within 4 business hours.</p>
    </section></PublicShell>
  );
  return (
    <PublicShell>
      <section className="hero-bg pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Trade your ride</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl mb-3"><span className="chrome-text">What's your car</span> <span className="gold-text">worth?</span></h1>
          <p className="text-slate-300">Get a written offer in under 4 hours. Apply the value as a credit against any Wakzy vehicle.</p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
        <form onSubmit={submit} className="surface p-8 rounded-md space-y-4" data-testid="trade-form">
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { k: "full_name", label: "Your name", req: true },
              { k: "email", label: "Email", type: "email", req: true },
              { k: "phone", label: "Phone" },
              { k: "vin", label: "VIN" },
              { k: "make", label: "Make", req: true },
              { k: "model", label: "Model", req: true },
              { k: "year", label: "Year", type: "number" },
              { k: "mileage", label: "Mileage", type: "number" },
              { k: "loan_payoff", label: "Outstanding loan payoff ($)", type: "number" },
            ].map((f) => (
              <div key={f.k}>
                <Label className="text-sm text-slate-300">{f.label}{f.req && <span className="text-[#E63946]"> *</span>}</Label>
                <Input type={f.type || "text"} required={f.req} value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                  className="bg-[#04060d] border-white/10 mt-1 h-11" data-testid={`trade-${f.k}-input`} />
              </div>
            ))}
            <div>
              <Label className="text-sm text-slate-300">Condition</Label>
              <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                <SelectTrigger className="bg-[#04060d] border-white/10 mt-1 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="poor">Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3 surface-elev p-4 rounded-sm">
            <Switch checked={form.accident_history} onCheckedChange={(v) => setForm({ ...form, accident_history: v })} />
            <span className="text-sm">Accident history</span>
          </div>
          <Button type="submit" disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="trade-submit-btn">
            {submitting ? "Submitting…" : "Request Appraisal"}
          </Button>
        </form>
      </section>
    </PublicShell>
  );
}
